import { Delete } from 'lucide-react';
import React, { useEffect } from 'react';

type Props = {
  onNumClick: (num: string) => void;
  onClear: () => void;
  onDelete: () => void;
  onEnter?: () => void;
};

export const NumericKeypad = React.memo(({ onNumClick, onClear, onDelete, onEnter }: Props) => {
  
  // テンキー用の共通アニメーションクラス（爆速＆微細な縮小）
  const baseKeyClass = "items-center justify-center transition-all duration-75 active:scale-[0.98] touch-manipulation";
  
  // キーボード入力の監視
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 1. 他の入力欄（検索ダイアログなど）にフォーカスがある場合は処理をスキップ
      const activeEl = document.activeElement as HTMLElement;
      const isInput = activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA';
      const isEditable = isInput && !(activeEl as HTMLInputElement).readOnly;
      if (isEditable) return;

      const isButton = activeEl.tagName === 'BUTTON' || activeEl.tagName === 'A';
      if (isButton && e.key === 'Enter') return;

      // 2. キーごとの処理
      if (/^[0-9]$/.test(e.key)) {
        // 数字キー (0-9) または テンキーの数字
        e.preventDefault(); // ブラウザのデフォルトスクロール等を防ぐ
        onNumClick(e.key);
      } else if (e.key === 'Backspace') {
        // Backspaceキー (1文字削除)
        e.preventDefault();
        onDelete();
      } else if (e.key === 'Escape' || e.key.toLowerCase() === 'c') {
        // Escapeキー または 'C'キー (全クリア)
        e.preventDefault();
        onClear();
      } else if (e.key === 'Enter') {
        // Enterキーが押されたら onEnter を呼ぶ
        e.preventDefault();
        if (onEnter) onEnter();
      }
    };

    // イベントリスナーを登録
    window.addEventListener('keydown', handleKeyDown);
    
    // コンポーネントが破棄される時にリスナーを解除（メモリリーク防止）
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onNumClick, onClear, onDelete, onEnter]); // 関数の参照が変わった時に再登録

  return (
    <div className="h-full w-full bg-slate-200 overflow-hidden">
      <div className="grid grid-cols-3 grid-rows-4 gap-[2px] h-full w-full">
        {[7, 8, 9, 4, 5, 6, 1, 2, 3].map((num) => (
          <button
            key={num}
            onPointerDown={(e) => {
              e.preventDefault(); // スマホでのゴーストクリックや長押し選択を防止
              onNumClick(num.toString());
            }}
            className={`bg-white hover:bg-slate-50 active:bg-slate-200 text-2xl text-slate-700 font-bold flex ${baseKeyClass}`}
          >
            {num}
          </button>
        ))}

        <button
          onPointerDown={(e) => { e.preventDefault(); onClear(); }}
          className={`bg-slate-300 hover:bg-slate-400 active:bg-slate-500 text-slate-700 flex-col gap-1 font-bold flex ${baseKeyClass}`}
        >
          <span className="text-lg">クリア</span>
        </button>

        <button
          onPointerDown={(e) => { e.preventDefault(); onNumClick('0'); }}
          className={`bg-white hover:bg-slate-50 active:bg-slate-200 text-2xl text-slate-700 font-bold flex ${baseKeyClass}`}
        >
          0
        </button>

        <button
          onPointerDown={(e) => { e.preventDefault(); onDelete(); }}
          className={`bg-slate-300 hover:bg-slate-400 active:bg-slate-500 text-slate-700 flex ${baseKeyClass}`}
        >
          <Delete className="w-8 h-8" />
        </button>
      </div>
    </div>
  );
});